import { useState, useMemo, useEffect } from 'react'
import dayjs from 'dayjs'
import { ScoopSelector } from '../../../../ui/ScoopSelector/ScoopSelector'
import { ScoopDialog } from '../../../../ui/ScoopDialog/ScoopDialog'
import { ScoopDatePicker } from '../../../../ui/ScoopDatePicker/ScoopDatePicker'
import { ScoopButton } from '../../../../ui/ScoopButton/ScoopButton'
import { ScoopSwitch } from '../../../../ui/ScoopSwitch/ScoopSwitch'
import { ScoopText } from '../../../../ui/ScoopText/ScoopText'
import { ScoopInfoTooltip } from '../../../../ui/ScoopInfoTooltip/ScoopInfoTooltip'
import { useExplorer } from '../../../../../context/explorer'
import styles from './ConfigTab.module.scss'

// Time range options
const TIME_RANGE_OPTIONS = [
    { label: 'Most Recent', value: 'Most Recent' },
    { label: 'Last Day', value: 'Last Day' },
    { label: 'Last Week', value: 'Last Week' },
    { label: 'Last Month', value: 'Last Month' },
    { label: 'Last Quarter', value: 'Last Quarter' },
    { label: 'Last Year', value: 'Last Year' },
    { label: 'Next Day', value: 'Next Day' },
    { label: 'Next Week', value: 'Next Week' },
    { label: 'Next Month', value: 'Next Month' },
    { label: 'Next Quarter', value: 'Next Quarter' },
    { label: 'Next Year', value: 'Next Year' },
    { label: 'All', value: 'All' },
    { label: 'Custom', value: 'Custom' },
]

// Period frequency options
const PERIOD_OPTIONS = [
    { label: 'Daily', value: 'Daily' },
    { label: 'Weekly', value: 'Weekly' },
    { label: 'Monthly', value: 'Monthly' },
    { label: 'Quarterly', value: 'Quarterly' },
    { label: 'Annually', value: 'Annually' },
]

// Period end options
const CALENDAR_TYPE_OPTIONS = [
    { label: 'Rolling', value: 'Rolling' },
    { label: 'Calendar', value: 'Calendar' },
]

// Relative period options for custom dialog
const RELATIVE_PERIOD_OPTIONS = [
    { value: 'LastYear', label: 'Last Year', index: 0 },
    { value: 'LastQuarter', label: 'Last Quarter', index: 1 },
    { value: 'LastMonth', label: 'Last Month', index: 2 },
    { value: 'LastWeek', label: 'Last Week', index: 3 },
    { value: 'Yesterday', label: 'Yesterday', index: 4 },
    { value: 'Now', label: 'Now', index: 5 },
    { value: 'Tomorrow', label: 'Tomorrow', index: 6 },
    { value: 'NextWeek', label: 'Next Week', index: 7 },
    { value: 'NextMonth', label: 'Next Month', index: 8 },
    { value: 'NextQuarter', label: 'Next Quarter', index: 9 },
    { value: 'NextYear', label: 'Next Year', index: 10 },
]

// Date comparison options for fixed mode
const DATE_COMPARISON_OPTIONS = [
    { value: 'GreaterThan', label: 'Greater Than' },
    { value: 'LessThan', label: 'Less Than' },
    { value: 'Equals', label: 'Equals' },
    { value: 'Between', label: 'Between' },
    { value: 'GreaterThanOrEquals', label: 'Greater Than or Equals' },
    { value: 'LessThanOrEquals', label: 'Less Than or Equals' },
]

/**
 * TimeRange component for configuring time range, period frequency, and period end.
 *
 * Uses ExplorerContext via useExplorer().
 * Uses setTimeRange, setPeriod, setCalendarType, setCustomRange, and setRelativeRange actions.
 */
export const TimeRange = () => {
    // Get state and actions from context
    const {
        queryConfig,
        dataConfig,
        setTimeRange,
        setPeriod,
        setCalendarType,
        setCustomRange,
        setRelativeRange,
    } = useExplorer()

    // Local state for custom range dialog
    const [customDialogOpen, setCustomDialogOpen] = useState(false)
    const [relative, setRelative] = useState(true)

    // Relative mode state
    const [startPeriod, setStartPeriod] = useState(RELATIVE_PERIOD_OPTIONS[0].value)
    const [endPeriod, setEndPeriod] = useState(RELATIVE_PERIOD_OPTIONS[5].value)

    // Fixed mode state
    const [dateComparison, setDateComparison] = useState('Between')
    const [dateRange, setDateRange] = useState(null)
    const [singleDate, setSingleDate] = useState(null)

    // Parse date string to Date object
    const parseDate = (dateString) => {
        if (!dateString) return null
        const [year, month, day] = dateString.split('-').map(Number)
        const parsedDate = new Date()
        parsedDate.setFullYear(year, month - 1, day)
        parsedDate.setHours(0, 0, 0, 0)
        return parsedDate
    }

    // Initialize dialog state from queryConfig when dialog opens
    useEffect(() => {
        if (customDialogOpen) {
            // Check if we have relative periods set
            if (queryConfig.fromPeriod || queryConfig.toPeriod) {
                setRelative(true)
                setStartPeriod(queryConfig.fromPeriod || RELATIVE_PERIOD_OPTIONS[0].value)
                setEndPeriod(queryConfig.toPeriod || RELATIVE_PERIOD_OPTIONS[5].value)
            } else if (queryConfig.from || queryConfig.to) {
                setRelative(false)
                const parsedFrom = parseDate(queryConfig.from)
                const parsedTo = parseDate(queryConfig.to)

                if (parsedFrom && parsedTo) {
                    if (queryConfig.from === queryConfig.to) {
                        setDateComparison('Equals')
                        setSingleDate(parsedFrom)
                    } else {
                        setDateComparison('Between')
                        setDateRange([parsedFrom, parsedTo])
                    }
                } else if (parsedFrom) {
                    setDateComparison('GreaterThanOrEquals')
                    setSingleDate(parsedFrom)
                } else if (parsedTo) {
                    setDateComparison('LessThanOrEquals')
                    setSingleDate(parsedTo)
                }
            } else {
                // Default to relative mode
                setRelative(true)
                setStartPeriod(RELATIVE_PERIOD_OPTIONS[0].value)
                setEndPeriod(RELATIVE_PERIOD_OPTIONS[5].value)
            }
        }
    }, [
        customDialogOpen,
        queryConfig.from,
        queryConfig.to,
        queryConfig.fromPeriod,
        queryConfig.toPeriod,
    ])

    // Handle time range change
    const handleTimeRangeChange = (e) => {
        const value = e.target.value
        if (value === 'Custom') {
            setCustomDialogOpen(true)
        } else {
            setTimeRange(value)
        }
    }

    // Handle custom range dialog apply
    const handleCustomRangeApply = () => {
        if (relative) {
            setRelativeRange(startPeriod, endPeriod)
        } else {
            let from = null
            let to = null

            if (dateComparison === 'Between') {
                if (dateRange && Array.isArray(dateRange) && dateRange.length === 2) {
                    from = dayjs(dateRange[0]).format('YYYY-MM-DD')
                    to = dayjs(dateRange[1]).format('YYYY-MM-DD')
                }
            } else if (singleDate) {
                switch (dateComparison) {
                    case 'GreaterThan':
                        from = dayjs(singleDate).add(1, 'day').format('YYYY-MM-DD')
                        to = null
                        break
                    case 'LessThan':
                        from = null
                        to = dayjs(singleDate).subtract(1, 'day').format('YYYY-MM-DD')
                        break
                    case 'Equals':
                        from = dayjs(singleDate).format('YYYY-MM-DD')
                        to = dayjs(singleDate).format('YYYY-MM-DD')
                        break
                    case 'GreaterThanOrEquals':
                        from = dayjs(singleDate).format('YYYY-MM-DD')
                        to = null
                        break
                    case 'LessThanOrEquals':
                        from = null
                        to = dayjs(singleDate).format('YYYY-MM-DD')
                        break
                }
            }

            if (from !== null || to !== null) {
                setCustomRange(from, to)
            }
        }
        setCustomDialogOpen(false)
    }

    // Handle custom range dialog cancel
    const handleCustomRangeCancel = () => {
        setCustomDialogOpen(false)
    }

    // Handle period frequency change
    const handlePeriodChange = (e) => {
        setPeriod(e.target.value)
    }

    // Handle period end (calendar type) change
    const handleCalendarTypeChange = (e) => {
        setCalendarType(e.target.value)
    }

    // Show period options only when category axis is Time and time series is enabled
    const showPeriodOptions = dataConfig.categoryAxis === 'Time' && queryConfig.tableTimeSeries

    // Get starting period index for filtering end period options
    const startIndex = useMemo(() => {
        return RELATIVE_PERIOD_OPTIONS.findIndex((opt) => opt.value === startPeriod)
    }, [startPeriod])

    // Filter end period options to only show periods >= start period
    const filteredEndPeriodOptions = useMemo(() => {
        return RELATIVE_PERIOD_OPTIONS.filter((opt) => opt.index >= startIndex)
    }, [startIndex])

    // Determine if apply button should be disabled
    const isApplyDisabled = useMemo(() => {
        if (relative) {
            return false // Relative mode always has valid defaults
        }
        if (dateComparison === 'Between') {
            return !dateRange || !Array.isArray(dateRange) || dateRange.length !== 2
        }
        return !singleDate
    }, [relative, dateComparison, dateRange, singleDate])

    return (
        <div className={styles.sectionContainer}>
            <ScoopSelector
                value={queryConfig.timeRange}
                onChange={handleTimeRangeChange}
                label="Time Range"
                options={TIME_RANGE_OPTIONS}
            />

            {showPeriodOptions && (
                <>
                    <ScoopSelector
                        value={queryConfig.period}
                        onChange={handlePeriodChange}
                        label="Period Frequency"
                        options={PERIOD_OPTIONS}
                    />

                    <ScoopSelector
                        value={queryConfig.calendarType}
                        onChange={handleCalendarTypeChange}
                        label="Period End"
                        options={CALENDAR_TYPE_OPTIONS}
                    />
                </>
            )}

            {/* Custom Date Range Dialog */}
            <ScoopDialog
                open={customDialogOpen}
                onClose={handleCustomRangeCancel}
                title="Filter Time Range"
                actions={
                    <>
                        <ScoopButton variant="outlined" onClick={handleCustomRangeCancel}>
                            Cancel
                        </ScoopButton>
                        <ScoopButton
                            variant="primary"
                            onClick={handleCustomRangeApply}
                            disabled={isApplyDisabled}
                        >
                            Apply
                        </ScoopButton>
                    </>
                }
            >
                <div className={styles.customRangeDialogContent}>
                    <ScoopText className={styles.customRangeDescription}>
                        Specify a custom time range
                    </ScoopText>

                    {/* Relative Dates Toggle */}
                    <div className={styles.relativeDatesRow}>
                        <ScoopText className={styles.relativeDatesLabel}>Relative Dates</ScoopText>
                        <ScoopInfoTooltip title="Specify a fixed date range vs. one based on relative dates" />
                        <ScoopSwitch
                            name="relative-dates-switch"
                            checked={relative}
                            onChange={() => setRelative(!relative)}
                        />
                    </div>

                    {/* Relative Mode - Period Selectors */}
                    {relative && (
                        <div className={styles.periodSelectorsRow}>
                            <ScoopSelector
                                value={startPeriod}
                                onChange={(e) => setStartPeriod(e.target.value)}
                                label="Starting"
                                options={RELATIVE_PERIOD_OPTIONS}
                            />
                            <ScoopSelector
                                value={endPeriod}
                                onChange={(e) => setEndPeriod(e.target.value)}
                                label="Ending"
                                options={filteredEndPeriodOptions}
                            />
                        </div>
                    )}

                    {/* Fixed Mode - Date Comparison */}
                    {!relative && (
                        <div className={styles.fixedDateRow}>
                            <ScoopSelector
                                value={dateComparison}
                                onChange={(e) => setDateComparison(e.target.value)}
                                label="Select option"
                                options={DATE_COMPARISON_OPTIONS}
                            />
                            <ScoopDatePicker
                                value={dateComparison === 'Between' ? dateRange : singleDate}
                                onChange={(newValue) =>
                                    dateComparison === 'Between'
                                        ? setDateRange(newValue)
                                        : setSingleDate(newValue)
                                }
                                range={dateComparison === 'Between'}
                                label={`Select ${dateComparison === 'Between' ? 'range' : 'date'}`}
                            />
                        </div>
                    )}
                </div>
            </ScoopDialog>
        </div>
    )
}
